/**
 * Eraya price-test bridge for AM Horizon.
 *
 * The SDK's PriceTestEngine only scans on init + when called. This theme
 * re-renders prices in two ways it can't see:
 *   1. Variant change -> Section Rendering fetch -> morph/replaceWith
 *      (assets/product-price.js, assets/product-card.js)
 *   2. AJAX-injected cards (recommendations, predictive search, quick-add,
 *      infinite scroll)
 *
 * Note this theme has NO `variant:update` dispatcher (only two dead
 * listeners). The live event is Shopify Standard Events
 * `shopify:product:select`, whose payload carries a deferred `promise` that
 * resolves AFTER the section fetch. We listen in the CAPTURE phase so our
 * `.then()` is registered before product-price.js's, which means our
 * data-variant-id write lands before the DOM swap it performs.
 */
(function () {
  'use strict';

  var PRICE_NODES =
    '[data-product-id], .price, .compare-at-price, .am-collection-tiles__product-card-price';

  function getManager() {
    try {
      var sdk = window.Eraya && window.Eraya.sdk;
      if (sdk && typeof sdk.getPriceTestManager === 'function') return sdk.getPriceTestManager();
    } catch (_) {}
    return null;
  }

  /** Normalize `gid://shopify/ProductVariant/123` or 123 -> "123". */
  function toId(value) {
    var m = String(value == null ? '' : value).match(/(\d+)\s*$/);
    return m ? m[1] : '';
  }

  // --- 1. Variant change -------------------------------------------------
  // Capture phase on document catches the event regardless of whether it
  // bubbles (capture always propagates down to the target).
  document.addEventListener(
    'shopify:product:select',
    function (event) {
      var promise = event && event.promise;
      if (!promise || typeof promise.then !== 'function') return;

      promise.then(function (resolved) {
        if (!resolved) return;
        var detail = resolved.detail || {};
        var variantId = toId(resolved.variant && resolved.variant.id);
        var productId = toId(detail.productId || (resolved.variant && resolved.variant.product_id));
        if (!productId || !variantId) return;

        document
          .querySelectorAll('[data-product-id="' + productId + '"]')
          .forEach(function (el) {
            el.setAttribute('data-variant-id', variantId);
          });

        var mgr = getManager();
        if (mgr && typeof mgr.updatePricesForProduct === 'function') {
          try {
            mgr.updatePricesForProduct(productId, variantId);
          } catch (_) {}
        }
      });
    },
    true
  );

  // --- 2. Re-render / injection observer ---------------------------------
  // Runs SYNCHRONOUSLY in the observer callback (a microtask, before paint)
  // so the shopper never sees the base price flash. A debounce here would
  // re-apply after paint and flash.
  var observer = null;
  var busy = false;

  function rescan() {
    if (busy) return;
    busy = true;
    try {
      var mgr = getManager();
      if (mgr && typeof mgr.updatePricesOnCurrentPage === 'function') {
        mgr.updatePricesOnCurrentPage();
      }
    } catch (_) {
    } finally {
      syncMixMatchCards();
      // Discard the mutations our own rewrite just queued, else the next
      // callback re-enters forever.
      if (observer) observer.takeRecords();
      busy = false;
    }
  }

  function isPriceNode(node) {
    if (!node || node.nodeType !== 1) return false;
    if (typeof node.matches === 'function' && node.matches(PRICE_NODES)) return true;
    return typeof node.querySelector === 'function' && node.querySelector(PRICE_NODES) !== null;
  }

  if (typeof MutationObserver !== 'undefined') {
    observer = new MutationObserver(function (mutations) {
      if (busy) return;
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (isPriceNode(added[j])) {
            rescan();
            return;
          }
        }
      }
    });

    var start = function () {
      // childList only — never attributes, which our own data-variant-id
      // writes would re-trigger.
      observer.observe(document.body, { childList: true, subtree: true });
    };
    if (document.body) start();
    else document.addEventListener('DOMContentLoaded', start);
  }

  // --- 2b. Mix & Match ----------------------------------------------------
  // The picker/summary blocks render prices from Liquid data, not from the
  // DOM the engine rewrites, so they need both halves:
  //
  //   a) unitCents() — the summary sums cents from its own JSON blob. Read the
  //      test price from the SAME source the cart stamps
  //      (getSimpleCartProperties) so what's shown is what's charged.
  //   b) syncMixMatchCards() — the picker re-renders cards and the details
  //      modal from data-mm-price, which would clobber the rewritten text.
  //      Copy the engine's output back into the attribute.

  /** Test price in cents for a summary JSON item, or 0 if not under test. */
  function unitCents(item) {
    var mgr = getManager();
    if (!mgr || typeof mgr.getSimpleCartProperties !== 'function' || !item) return 0;
    var productId = toId(item.product_id);
    var variantId = toId(item.variant_id);
    if (!productId || !variantId) return 0; // fail closed — no guessed variant
    try {
      var props = mgr.getSimpleCartProperties(productId, variantId);
      var cents = props && Number(props._eraya_test_price);
      return cents > 0 ? cents : 0;
    } catch (_) {
      return 0;
    }
  }
  window.ErayaMM = { unitCents: unitCents };

  function syncMixMatchCards() {
    document.querySelectorAll('.am-mm-picker__card[data-product-id]').forEach(function (card) {
      var priceEl = card.querySelector('.am-mm-picker__price');
      if (!priceEl) return;
      var text = priceEl.textContent.trim();
      if (text && text !== card.getAttribute('data-mm-price')) {
        card.setAttribute('data-mm-price', text);
      }
    });
  }

  // Capture phase runs before the picker's own filter/sort/modal handlers, so
  // the attribute is already current by the time they read it.
  document.addEventListener(
    'click',
    function (event) {
      var t = event.target;
      if (t && t.nodeType === 1 && typeof t.closest === 'function' && t.closest('.am-mm-picker')) {
        syncMixMatchCards();
      }
    },
    true
  );

  // --- 3. Buy It Now / accelerated checkout -------------------------------
  // These bypass /cart/add entirely, so the SDK's fetch patch never sees them
  // and the shopper would be CHARGED the original price. Intercept, add the
  // line with _eraya_* properties, then go to checkout.
  //
  // ponytail: adds to the existing cart rather than an isolated single-item
  // express checkout. Upgrade to a Storefront API cartCreate in the SDK if
  // mixed-cart express checkout becomes a problem.
  var EXPRESS_SELECTOR =
    'shopify-buy-it-now-button, shopify-accelerated-checkout, shopify-accelerated-checkout-cart,' +
    '[data-shopify="payment-button"], .shopify-payment-button, .shopify-payment-button__button';

  function findExpressTarget(event) {
    var path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    for (var i = 0; i < path.length; i++) {
      var node = path[i];
      if (node && node.nodeType === 1 && typeof node.matches === 'function') {
        if (node.matches(EXPRESS_SELECTOR)) return node;
      }
    }
    return null;
  }

  function findForm(node) {
    var el = node;
    while (el) {
      if (typeof el.closest === 'function') {
        var form = el.closest('form[action*="/cart/add"]');
        if (form) return form;
      }
      // Hop out of a shadow root.
      el = el.parentNode && el.parentNode.host ? el.parentNode.host : el.parentElement;
    }
    var forms = document.querySelectorAll('form[action*="/cart/add"]');
    return forms.length === 1 ? forms[0] : null;
  }

  document.addEventListener(
    'click',
    function (event) {
      var target = findExpressTarget(event);
      if (!target) return;

      var mgr = getManager();
      if (!mgr || typeof mgr.getSimpleCartProperties !== 'function') return;

      var form = findForm(target);
      if (!form) return;

      var variantInput = form.querySelector('[name="id"]');
      var variantId = toId(variantInput && variantInput.value);
      if (!variantId) return; // fail closed — never stamp without an exact variant

      var productId = '';
      try {
        if (typeof mgr.getProductIdFromVariantId === 'function') {
          productId = toId(mgr.getProductIdFromVariantId(variantId));
        }
      } catch (_) {}
      if (!productId) return;

      var properties = null;
      try {
        properties = mgr.getSimpleCartProperties(productId, variantId);
      } catch (_) {}
      // Not a test product (or no resolvable price) — let native express checkout run.
      if (!properties || !Object.keys(properties).length) return;

      var quantityInput = form.querySelector('[name="quantity"]');
      var quantity = parseInt((quantityInput && quantityInput.value) || '1', 10) || 1;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      var addUrl =
        (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) || '/cart/add.js';
      var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

      fetch(addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: quantity, properties: properties }],
        }),
      })
        .then(function () {
          window.location.href = root.replace(/\/$/, '') + '/checkout';
        })
        .catch(function () {
          window.location.href = root.replace(/\/$/, '') + '/cart';
        });
    },
    true
  );
})();
